/**
 * Shared icon + colour for a notification, so the header dropdown and the
 * full page render each row the same way.
 */

import type { ReactNode } from "react";
import {
  IconAlertTriangle,
  IconChecklist,
  IconCircleCheck,
  IconClipboardCheck,
  IconClock,
  IconSignature,
} from "@tabler/icons-react";
import type { AppNotification } from "./useNotifications";

export function notificationColor(n: AppNotification): string {
  switch (n.kind) {
    case "failed":
      return "red";
    case "finished":
      return "green";
    case "test":
      return "blue";
    case "gate":
      if (n.status === "rejected") return "red";
      if (n.status === "reported") return "green";
      return "yellow";
  }
}

export function notificationIcon(n: AppNotification, size = 16): ReactNode {
  switch (n.kind) {
    case "failed":
      return <IconAlertTriangle size={size} />;
    case "finished":
      return n.status === "succeeded" ? <IconCircleCheck size={size} /> : <IconClock size={size} />;
    case "test":
      return <IconChecklist size={size} />;
    case "gate":
      if (n.status === "reported") return <IconSignature size={size} />;
      if (n.status === "rejected") return <IconAlertTriangle size={size} />;
      return <IconClipboardCheck size={size} />;
  }
}

/**
 * When it happened, in as few characters as a log row can spare. Relative up to
 * a day (the answer is usually "how long ago"), then the calendar date — "six
 * days ago" is a worse answer than "17 Sep".
 */
export function notificationWhen(at: string | null, now: Date = new Date()): string {
  if (!at) return "";
  const then = new Date(at);
  if (Number.isNaN(then.getTime())) return "";
  const seconds = Math.round((now.getTime() - then.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return then.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
